// HMR: three-axis-demo fixture
import { DIM_METADATA } from "./dim-metadata";
import type {
  AxisScore,
  Evidence,
  FrameworkSkill,
  SensitivityResult,
  SkillBullet,
  ThreeAxisRecommendation,
} from "./three-axis";
import { findBorderlineAxis, findExceptionalAxis } from "./three-axis";

// Framework rubric bullets for Forward Deployed — same content the real LLM
// pipeline generates from the DB framework markdown. Kept inline here so the
// demo fixture renders the full rubric (criteria per level) without depending
// on a live DB call at render time.
const FDE_FRAMEWORK_BULLETS: Record<string, Record<"L3" | "L4" | "L5", string[]>> = {
  project_management: {
    L3: [
      "Delivers pieces when scope is clear.",
      "Follows defined processes without drift.",
      "Escalates blockers to the project lead.",
      "Tracks status of own tasks reliably.",
      "Meets committed deadlines consistently.",
    ],
    L4: [
      "Owns projects end-to-end without needing oversight.",
      "Identifies and communicates risks early — blockers never come as surprises.",
      "Re-prioritizes quickly when context shifts without losing control.",
      "Brings structure to messy situations that others are struggling to navigate.",
      "Stakeholders rely on them as the source of truth on project status.",
    ],
    L5: [
      "Designs repeatable project playbooks for the org.",
      "Mentors L4s on delivery craft across teams.",
      "Builds portfolio-level prioritization frameworks.",
      "Diagnoses recurring delivery failures at a system level.",
      "Sets multi-year delivery vision the org orients around.",
    ],
  },
  stakeholder_management: {
    L3: [
      "Builds real relationships outside of scheduled touchpoints.",
      "Moves decisions forward, doesn't just facilitate discussions.",
      "Keeps stakeholders aligned on scope and status without hand-holding.",
      "Navigates predictable stakeholder dynamics with confidence.",
      "Follows up on commitments without needing reminders.",
    ],
    L4: [
      "Reads what is not being said in meetings and acts on it.",
      "Addresses tensions early, before they surface.",
      "Is seen as someone in control of the room.",
      "Shifts stakeholder positions with a clear point of view.",
      "Builds trust fast enough to unlock candid conversation.",
    ],
    L5: [
      "Treated as a peer by C-levels and board members.",
      "Shapes boardroom narratives, doesn't just present in them.",
      "Navigates shareholder / regulator politics without coaching.",
      "Carries cross-org agendas without explicit authority.",
      "Is sought by exec peers for judgment on relational risk.",
    ],
  },
  business_acumen: {
    L3: [
      "Uses correct business vocabulary (margin, CAC, retention, runway).",
      "Reads standard financial reports without help.",
      "Knows the main drivers of the client's P&L.",
      "Asks the business first, the solution second.",
      "Can describe the client's business model in 2 sentences.",
    ],
    L4: [
      "Explains the impact of every initiative in business terms.",
      "Challenges ideas that don't align with business reality.",
      "Simplifies complex business models into clear, actionable insights.",
      "Stakeholders feel you get their business.",
      "Understands the client's cost structure and key revenue drivers in depth.",
    ],
    L5: [
      "Models unit economics (CAC/LTV/payback) from first principles.",
      "Constructs multi-year P&L scenarios independently.",
      "Argues capital-allocation calls with the CFO as a peer.",
      "Reframes the client's strategic thesis, not just implements it.",
      "Spots revenue leaks or cost traps before the client sees them.",
    ],
  },
  hr_fluency: {
    L3: [
      "Uses HR vocabulary (comp, leveling, perf cycles) correctly.",
      "Understands the basic HR operating rhythm of a company.",
      "Recognizes common HR anti-patterns when named.",
      "Can brief an exec on 'what HR is doing right now'.",
      "Reads a salary band without confusion.",
    ],
    L4: [
      "Articulates the difference between compensation philosophy and comp structure.",
      "Explains how job architecture shapes career paths.",
      "Maps HR processes (hiring, perf, comp, org design) to business outcomes.",
      "Debates org-design tradeoffs in business language, not HR jargon.",
      "Has enough depth to challenge a generic HR consultant's framing.",
    ],
    L5: [
      "Designs comp structures from first principles, not off-the-shelf.",
      "Articulates tradeoffs between leveling frameworks with authority.",
      "Writes opinionated memos on org-design that shape industry thinking.",
      "Is called on by peers for second opinions on HR strategy.",
      "Runs comp/org-design reviews that senior HR leaders defer to.",
    ],
  },
  product_sense: {
    L3: [
      "Follows established tool recipes reliably.",
      "Runs a defined workflow end-to-end without errors.",
      "Knows when to ask before deviating from the template.",
      "Uses the standard tool for the job without reinventing.",
      "Recognizes when the wrong tool is being used.",
    ],
    L4: [
      "Chooses the simplest tool that solves the problem at stake.",
      "Pushes back on custom requests that don't scale.",
      "Frames workflow decisions by cost of reversal.",
      "Identifies when a process should become a product (and vice versa).",
      "Balances speed of delivery against long-term maintainability.",
    ],
    L5: [
      "Treats tools as investments, evaluates by payback period.",
      "Builds reusable internal systems that compound team leverage.",
      "Models total cost of ownership before adopting any tool.",
      "Sets org-wide tooling strategy, not just team-level choices.",
      "Anticipates which one-off solutions will calcify into tech debt.",
    ],
  },
};

/** Emit framework bullets for a skill, tagging the first `metCount` criteria at
 *  each level as met and the remainder as not met — keeps the rendered criteria
 *  counts consistent with `levelCoverage`. */
function frameworkBulletsFor(
  skillId: string,
  metCounts: { L3: number; L4: number; L5: number },
): SkillBullet[] {
  const rubric = FDE_FRAMEWORK_BULLETS[skillId];
  if (!rubric) return [];
  const out: SkillBullet[] = [];
  (["L3", "L4", "L5"] as const).forEach((lk) => {
    const metCount = metCounts[lk] ?? 0;
    rubric[lk].forEach((text, idx) => {
      out.push({
        id: `${skillId}_fw_${lk}_${idx}`,
        text,
        level: lk,
        met: idx < metCount,
        kind: "framework",
      });
    });
  });
  return out;
}

// Demo rubrics + descriptions are sourced from `dim-metadata.ts` so the
// design-review demo always matches what fresh evaluations and refreshed
// stored evaluations show. To rewrite a rubric, edit `DIM_METADATA`.
const ACADEMIC_RUBRIC = DIM_METADATA.academic.rubric;
const TRAJECTORY_RUBRIC = DIM_METADATA.trajectory.rubric;
const TENURE_RUBRIC = DIM_METADATA.tenure.rubric;
const FOUNDER_RUBRIC = DIM_METADATA.founder.rubric;

const HIRE_EVIDENCES: Evidence[] = [
  {
    id: "academic",
    name: "Acadêmico",
    description: DIM_METADATA.academic.description,
    rubric: ACADEMIC_RUBRIC,
    score: 4.0,
    selectedLevel: 4,
    contributions: [{ axis: "gca", weight: 0.3 }],
    sources: [
      {
        quote: "Bacharelado em curso competitivo — Universidade Tier 1 nacional.",
        citation: "Universidade Tier 1 · formação de graduação",
      },
      {
        quote: "LLM em área técnica — Universidade Tier 1 internacional.",
        citation: "Universidade Tier 1 · pós-graduação",
      },
    ],
  },
  {
    // v2 cutover: company_era (score 4.5) + growth_within (score 3.5) merged.
    // trajectory = avg(4.5, 3.5) = 4.0 → GCA = 0.30·0.8 + 0.70·0.8 = 0.80.
    id: "trajectory",
    name: "Trajetória",
    description: DIM_METADATA.trajectory.description,
    rubric: TRAJECTORY_RUBRIC,
    score: 4.0,
    selectedLevel: 4,
    contributions: [
      { axis: "gca", weight: 0.7 },
      { axis: "compWay", weight: 0.3 },
    ],
    sources: [
      {
        quote: "Senior Advisor @ Empresa Tier 1 Y — venture builder em fase de expansão global.",
        citation: "Empresa Tier 1 Y",
      },
      {
        quote: "Analyst @ Empresa Tier 1 X — regulador em fase de modernização.",
        citation: "Empresa Tier 1 X",
      },
      {
        quote: "Product role em fintech early-stage (seed → series B era).",
        citation: "Empresa Z · fintech early-stage",
      },
      {
        quote: "Promovido 2x em 5 anos na posição de Analyst em regulador Tier 1.",
        citation: "Empresa Tier 1 X · histórico de promoções",
      },
    ],
  },
  {
    id: "tenure",
    name: "Estabilidade",
    description: DIM_METADATA.tenure.description,
    rubric: TENURE_RUBRIC,
    score: 4.0,
    selectedLevel: 4,
    contributions: [{ axis: "compWay", weight: 0.3 }],
    sources: [
      {
        quote: "≥5 anos em regulador Tier 1 X; transição limpa para venture builder Tier 1 Y.",
        citation: "resumo do perfil",
      },
    ],
  },
  {
    id: "founder",
    name: "Sinal de fundador / construtor",
    description: DIM_METADATA.founder.description,
    rubric: FOUNDER_RUBRIC,
    score: 4.0,
    selectedLevel: 4,
    contributions: [{ axis: "compWay", weight: 0.4 }],
    sources: [
      {
        quote:
          "Passagem por fintech early-stage (seed → series B) em papel de construtor de produto.",
        citation: "Empresa Z · fintech early-stage",
      },
    ],
  },
];

const DEMO_LEVEL_META: Record<string, FrameworkSkill["levelMeta"]> = {
  project_management: {
    L3: {
      title: "Contributor",
      description: "Entrega peças bem definidas com supervisão.",
      score: 3,
    },
    L4: {
      title: "End-to-End Owner",
      description: "Dirige workstreams inteiros autonomamente; traz ordem à ambiguidade.",
      score: 4,
    },
    L5: {
      title: "Systems Thinker",
      description: "Redesenha como a organização aborda entrega de projetos.",
      score: 5,
    },
  },
  stakeholder_management: {
    L3: {
      title: "Relator",
      description: "Conduz conversas técnicas e mantém alinhamento operacional.",
      score: 2,
    },
    L4: {
      title: "Influenciador",
      description: "Lê dinâmicas não ditas e influencia decisões sênior.",
      score: 3,
    },
    L5: {
      title: "Executive Presence",
      description: "Tratado como par por C-levels; molda narrativas de boardroom.",
      score: 4,
    },
  },
  business_acumen: {
    L3: {
      title: "Literate",
      description: "Entende os drivers básicos do negócio do cliente.",
      score: 2,
    },
    L4: {
      title: "Business Partner",
      description: "Usa contexto de negócio para moldar decisões; argumenta em impacto.",
      score: 3,
    },
    L5: {
      title: "Strategic Thinker",
      description: "Modela estrutura de custos/receita em profundidade e desafia a tese.",
      score: 4,
    },
  },
  hr_fluency: {
    L3: {
      title: "Informed",
      description: "Conhece os conceitos básicos de HR e vocabulário do setor.",
      score: 0,
    },
    L4: {
      title: "Conceptual",
      description: "Pensa HR como sistema que molda resultados de negócio.",
      score: 2,
    },
    L5: {
      title: "Applied Thinker",
      description: "Profundidade técnica em comp / org design; desafia consultoria.",
      score: 3,
    },
  },
  product_sense: {
    L3: {
      title: "Tool Operator",
      description: "Usa ferramentas corretamente, segue receitas existentes.",
      score: 2,
    },
    L4: {
      title: "Solution Designer",
      description:
        "Escolhe a ferramenta certa pelo problema em jogo; rejeita custom não-escalável.",
      score: 3,
    },
    L5: {
      title: "Systems Builder",
      description: "Trata ferramentas como investimento; avalia por payback.",
      score: 4,
    },
  },
};

const HIRE_FRAMEWORK: FrameworkSkill[] = [
  {
    id: "project_management",
    name: "Project Management",
    required: "L4",
    assigned: "L4",
    levelMeta: DEMO_LEVEL_META.project_management,
    levelCoverage: {
      L1: { metCount: 0, totalCount: 0 },
      L2: { metCount: 0, totalCount: 0 },
      L3: { metCount: 5, totalCount: 5 },
      L4: { metCount: 4, totalCount: 5 },
      L5: { metCount: 1, totalCount: 5 },
    },
    bullets: [
      ...frameworkBulletsFor("project_management", { L3: 5, L4: 4, L5: 1 }),
      {
        id: "pm_obs_e2e",
        text: "Owns projects end-to-end without needing oversight.",
        level: "L4",
        met: true,
        kind: "observation",
        source: {
          quote:
            "Conduziu end-to-end o lançamento de um fundo vertical em empresa Tier 1 Y, sem sponsor sênior por 18 meses.",
          citation: "Empresa Tier 1 Y · Senior Advisor",
        },
      },
      {
        id: "pm_obs_risk",
        text: "Identifies and communicates risks early — blockers never come as surprises.",
        level: "L4",
        met: true,
        kind: "observation",
        source: {
          quote:
            "Antecipou risco regulatório 6 meses antes da vigência, coordenando alinhamento cross-desk.",
          citation: "Empresa Tier 1 X · Senior Analyst",
        },
      },
      {
        id: "pm_obs_repivot",
        text: "Re-prioritizes quickly when context shifts without losing control.",
        level: "L4",
        met: true,
        kind: "observation",
        source: {
          quote: "Pivotou tese de investimento após mudança macro e reposicionou em 60 dias.",
          citation: "resumo do perfil",
        },
      },
      {
        id: "pm_obs_gap_sot",
        text: "Stakeholders rely on them as the source of truth on project status.",
        level: "L4",
        met: false,
        kind: "observation",
        gapRationale:
          "Execução forte, mas sem citação de stakeholders tratando a pessoa como referência de status.",
      },
    ],
  },
  {
    id: "stakeholder_management",
    name: "Agency to influence stakeholders",
    required: "L4",
    assigned: "L4",
    levelMeta: DEMO_LEVEL_META.stakeholder_management,
    levelCoverage: {
      L1: { metCount: 0, totalCount: 0 },
      L2: { metCount: 0, totalCount: 0 },
      L3: { metCount: 5, totalCount: 5 },
      L4: { metCount: 2, totalCount: 5 },
      L5: { metCount: 0, totalCount: 5 },
    },
    bullets: [
      ...frameworkBulletsFor("stakeholder_management", { L3: 5, L4: 2, L5: 0 }),
      {
        id: "sm_obs_reads_room",
        text: "Reads what is not being said in meetings and acts on it.",
        level: "L4",
        met: true,
        kind: "observation",
        source: {
          quote:
            "Levou comitê regulatório a consenso em 4 semanas após 8 meses de deadlock — lendo posições não ditas antes de propor saída.",
          citation: "Empresa Tier 1 X · Senior Analyst",
        },
      },
      {
        id: "sm_obs_tension_early",
        text: "Addresses tensions early, before they surface.",
        level: "L4",
        met: true,
        kind: "observation",
        source: {
          quote:
            "Antecipou conflito de incentivos em comitê de investimento 3 semanas antes da votação formal.",
          citation: "Empresa Tier 1 Y · Senior Advisor",
        },
      },
      {
        id: "sm_obs_control",
        text: "Is seen as someone in control of the room.",
        level: "L4",
        met: false,
        kind: "observation",
        gapRationale: "Sem depoimentos sobre presença em sala com liderança sênior.",
      },
      {
        id: "sm_obs_shift_positions",
        text: "Shifts stakeholder positions with a clear point of view.",
        level: "L4",
        met: false,
        kind: "observation",
        gapRationale:
          "Consensos narrados por facilitação, não por posição defendida com opinião explícita.",
      },
      {
        id: "sm_obs_trust_candid",
        text: "Builds trust fast enough to unlock candid conversation.",
        level: "L4",
        met: false,
        kind: "observation",
        gapRationale: "Relações descritas como transacionais; faltam sinais de confiança profunda.",
      },
    ],
  },
  {
    id: "business_acumen",
    name: "Business Acumen",
    required: "L3",
    assigned: "L4",
    levelMeta: DEMO_LEVEL_META.business_acumen,
    levelCoverage: {
      L1: { metCount: 0, totalCount: 0 },
      L2: { metCount: 0, totalCount: 0 },
      L3: { metCount: 5, totalCount: 5 },
      L4: { metCount: 4, totalCount: 5 },
      L5: { metCount: 1, totalCount: 5 },
    },
    bullets: [
      ...frameworkBulletsFor("business_acumen", { L3: 5, L4: 4, L5: 1 }),
      {
        id: "ba_l4_pl_framing",
        text: "Explains the impact of every initiative in business terms.",
        level: "L4",
        met: true,
        kind: "observation",
        source: {
          quote:
            "Memos enquadram mudanças regulatórias como deltas de P&L das investidas, não como itens de compliance.",
          citation: "Empresa Tier 1 Y · Senior Advisor",
        },
      },
      {
        id: "ba_l4_challenge",
        text: "Challenges ideas that don't align with business reality.",
        level: "L4",
        met: true,
        kind: "observation",
        source: {
          quote:
            "Bloqueou 2 decisões de comitê de investimento em unit economics; ambas revertidas depois.",
          citation: "Empresa Tier 1 Y · Senior Advisor",
        },
      },
      {
        id: "ba_l4_simplify",
        text: "Simplifies complex business models into clear, actionable insights.",
        level: "L4",
        met: true,
        kind: "observation",
        source: {
          quote: "Traduziu prospectos de 400 páginas em briefs de 2 páginas usados pela liderança.",
          citation: "Empresa Tier 1 X · Senior Analyst",
        },
      },
      {
        id: "ba_l4_gets_business",
        text: "Stakeholders feel you get their business.",
        level: "L4",
        met: true,
        kind: "observation",
        source: {
          quote:
            "3 CEOs de portfolio listaram a pessoa como primeira chamada para revisão de estratégia comercial.",
          citation: "resumo do perfil",
        },
      },
      {
        id: "ba_l4_cost_structure",
        text: "Understands the client's cost structure and key revenue drivers in depth.",
        level: "L4",
        met: false,
        kind: "observation",
        gapRationale:
          "Foco em enquadrar impacto; faltam exemplos de modelagem profunda de CAC/LTV ou unit economics nas investidas.",
      },
    ],
  },
  {
    id: "hr_fluency",
    name: "HR Fluency",
    required: "L3",
    assigned: "L4",
    levelMeta: DEMO_LEVEL_META.hr_fluency,
    levelCoverage: {
      L1: { metCount: 0, totalCount: 0 },
      L2: { metCount: 0, totalCount: 0 },
      L3: { metCount: 5, totalCount: 5 },
      L4: { metCount: 4, totalCount: 5 },
      L5: { metCount: 0, totalCount: 5 },
    },
    bullets: [
      ...frameworkBulletsFor("hr_fluency", { L3: 5, L4: 4, L5: 0 }),
      {
        id: "hr_l4_philosophy",
        text: "Articulates the difference between compensation philosophy and comp structure.",
        level: "L4",
        met: true,
        kind: "observation",
        source: {
          quote:
            "Co-autor de memo de comp que reniveou 40+ posições e introduziu bônus performance-linked — framing de filosofia, não apenas faixas.",
          citation: "Empresa Tier 1 X · Senior Analyst",
        },
      },
      {
        id: "hr_l4_job_arch",
        text: "Explains how job architecture shapes career paths.",
        level: "L4",
        met: true,
        kind: "observation",
        source: {
          quote:
            "Aconselha fundadores na transição de arquitetura role-based para level-based e como isso destrava contratações sênior.",
          citation: "Empresa Tier 1 Y · Senior Advisor",
        },
      },
      {
        id: "hr_l4_business_outcomes",
        text: "Maps HR processes (hiring, perf, comp, org design) to business outcomes.",
        level: "L4",
        met: true,
        kind: "observation",
        source: {
          quote:
            "Reenquadrou risco de retenção como risco operacional, movendo a conversa do comitê de HR para o comitê executivo de risco.",
          citation: "Empresa Tier 1 X · Senior Analyst",
        },
      },
      {
        id: "hr_l4_debate_lang",
        text: "Debates org-design tradeoffs in business language, not HR jargon.",
        level: "L4",
        met: true,
        kind: "observation",
        source: {
          quote:
            "Aconselha CEOs de portfolio em transições founder-para-gerente-profissional via lente de negócio.",
          citation: "Empresa Tier 1 Y · Senior Advisor",
        },
      },
      {
        id: "hr_l4_challenge_consultant",
        text: "Has enough depth to challenge a generic HR consultant's framing.",
        level: "L4",
        met: false,
        kind: "observation",
        gapRationale:
          "Sem registro de engajamento direto com consultoria de HR ou fornecedor de comp.",
      },
    ],
  },
  {
    id: "product_sense",
    name: "Product Sense",
    required: "L3",
    assigned: "L4",
    levelMeta: DEMO_LEVEL_META.product_sense,
    levelCoverage: {
      L1: { metCount: 0, totalCount: 0 },
      L2: { metCount: 0, totalCount: 0 },
      L3: { metCount: 5, totalCount: 5 },
      L4: { metCount: 4, totalCount: 5 },
      L5: { metCount: 1, totalCount: 5 },
    },
    bullets: [
      ...frameworkBulletsFor("product_sense", { L3: 5, L4: 4, L5: 1 }),
      {
        id: "ps_l4_simplest_tool",
        text: "Chooses the simplest tool that solves the problem at stake.",
        level: "L4",
        met: true,
        kind: "observation",
        source: {
          quote:
            "Vetou proposta de CRM custom em favor de combinação off-the-shelf — 90% dos requisitos a 5% do custo.",
          citation: "Empresa Tier 1 Y · Senior Advisor",
        },
      },
      {
        id: "ps_l4_pushback_custom",
        text: "Pushes back on custom requests that don't scale.",
        level: "L4",
        met: true,
        kind: "observation",
        source: {
          quote: "Rejeitou 3 builds de reporting bespoke; migrou o time para templates padrão.",
          citation: "Empresa Tier 1 X · Senior Analyst",
        },
      },
      {
        id: "ps_l4_cost_of_reversal",
        text: "Frames workflow decisions by cost of reversal.",
        level: "L4",
        met: true,
        kind: "observation",
        source: {
          quote:
            "Pilota mudanças de processo com 1-2 empresas antes de rollout. 'Test before scale' é princípio explícito.",
          citation: "resumo do perfil",
        },
      },
      {
        id: "ps_l4_process_to_product",
        text: "Identifies when a process should become a product (and vice versa).",
        level: "L4",
        met: true,
        kind: "observation",
        source: {
          quote:
            "Produtizou o framework de sandbox regulatório; saiu de playbook ad-hoc para serviço interno padrão.",
          citation: "Empresa Tier 1 X · Senior Analyst",
        },
      },
      {
        id: "ps_l4_balance",
        text: "Balances speed of delivery against long-term maintainability.",
        level: "L4",
        met: false,
        kind: "observation",
        gapRationale:
          "Escolhas descritas por simplicidade e custo, sem enquadramento explícito de manutenção de longo prazo.",
      },
    ],
  },
];

const HIRE_SENSITIVITY: SensitivityResult[] = [
  {
    axis: "compWay",
    perturbations: [
      {
        evidenceId: "tenure",
        delta: -0.5,
        resultingScore: 0.78,
        flipsVerdict: true,
      },
      {
        evidenceId: "founder",
        delta: -0.5,
        resultingScore: 0.79,
        flipsVerdict: true,
      },
      {
        evidenceId: "tenure+founder",
        delta: -0.5,
        resultingScore: 0.74,
        flipsVerdict: true,
      },
      {
        evidenceId: "tenure",
        delta: 0.5,
        resultingScore: 0.87,
        flipsVerdict: false,
      },
    ],
    fragilityRatio: "3 de 4",
    fragile: true,
  },
];

const NO_HIRE_EVIDENCES: Evidence[] = [
  {
    id: "academic",
    name: "Acadêmico",
    description: DIM_METADATA.academic.description,
    rubric: ACADEMIC_RUBRIC,
    score: 3.0,
    selectedLevel: 3,
    contributions: [{ axis: "gca", weight: 0.3 }],
    sources: [
      {
        quote: "Bacharelado em Engenharia de Produção — universidade Tier 2 nacional.",
        citation: "Universidade Tier 2 · graduação",
      },
    ],
  },
  {
    id: "tenure",
    name: "Estabilidade",
    description: DIM_METADATA.tenure.description,
    rubric: TENURE_RUBRIC,
    score: 2.0,
    selectedLevel: 2,
    contributions: [{ axis: "compWay", weight: 0.3 }],
    sources: [
      {
        quote: "PM @ Empresa Tier 1 Hyper-Growth — 4 meses (atual).",
        citation: "Empresa Tier 1 Hyper-Growth · atual",
      },
      {
        quote: "Product Lead @ Empresa Tier 1 Consolidated — 7 meses.",
        citation: "Empresa Tier 1 Consolidated",
      },
      {
        quote: "PM @ Empresa Tier 2 pós-IPO — 22 meses.",
        citation: "Empresa Tier 2 pós-IPO",
      },
      {
        quote: "Senior Consultant @ Consultoria Tier 2 — 2 anos.",
        citation: "Consultoria Tier 2",
      },
    ],
  },
  {
    id: "founder",
    name: "Sinal de fundador / construtor",
    description: DIM_METADATA.founder.description,
    rubric: FOUNDER_RUBRIC,
    score: 1.5,
    selectedLevel: 2,
    contributions: [{ axis: "compWay", weight: 0.4 }],
    sources: [
      {
        quote:
          "Sempre em empresas com 500+ funcionários ao longo da carreira. Sem passagem por early-stage ou papel de founding team.",
        citation: "análise do perfil",
      },
    ],
  },
  {
    // v2 cutover: company_era (score 3.5) + growth_within (score 3.5) merged.
    // trajectory = avg(3.5, 3.5) = 3.5 → GCA = 0.30·0.6 + 0.70·0.7 = 0.67.
    id: "trajectory",
    name: "Trajetória",
    description: DIM_METADATA.trajectory.description,
    rubric: TRAJECTORY_RUBRIC,
    score: 3.5,
    selectedLevel: 4,
    contributions: [
      { axis: "gca", weight: 0.7 },
      { axis: "compWay", weight: 0.3 },
    ],
    sources: [
      {
        quote: "Empresa Tier 1 Hyper-Growth — era de forte expansão.",
        citation: "Empresa Tier 1 Hyper-Growth",
      },
      {
        quote: "Empresa Tier 2 pós-IPO — era de consolidação.",
        citation: "Empresa Tier 2 pós-IPO",
      },
      {
        quote: "Consultoria Tier 2 — era de maturidade.",
        citation: "Consultoria Tier 2",
      },
      {
        quote: "Progressão de Analyst para PM em ciclos de 2-3 anos dentro das empresas.",
        citation: "histórico de cargos",
      },
    ],
  },
];

const NO_HIRE_FRAMEWORK: FrameworkSkill[] = [
  {
    id: "project_management",
    name: "Project Management",
    required: "L4",
    assigned: "L4",
    levelMeta: DEMO_LEVEL_META.project_management,
    levelCoverage: {
      L1: { metCount: 0, totalCount: 0 },
      L2: { metCount: 0, totalCount: 0 },
      L3: { metCount: 5, totalCount: 5 },
      L4: { metCount: 4, totalCount: 5 },
      L5: { metCount: 0, totalCount: 5 },
    },
    bullets: [
      {
        id: "pm_l4_owns_e2e",
        text: "Owns projects end-to-end without needing oversight.",
        level: "L4",
        met: true,
      },
      {
        id: "pm_l4_risk_early",
        text: "Identifies and communicates risks early.",
        level: "L4",
        met: true,
      },
      {
        id: "pm_l4_reprioritize",
        text: "Re-prioritizes quickly when context shifts without losing control.",
        level: "L4",
        met: true,
      },
      {
        id: "pm_l4_structure",
        text: "Brings structure to messy situations that others are struggling to navigate.",
        level: "L4",
        met: true,
      },
      {
        id: "pm_l4_source_of_truth",
        text: "Stakeholders rely on them as the source of truth on project status.",
        level: "L4",
        met: false,
        gapRationale: "Sem citação direta de stakeholders.",
      },
    ],
  },
  {
    id: "stakeholder_management",
    name: "Agency to influence stakeholders",
    required: "L4",
    assigned: "L3",
    levelMeta: DEMO_LEVEL_META.stakeholder_management,
    levelCoverage: {
      L1: { metCount: 0, totalCount: 0 },
      L2: { metCount: 0, totalCount: 0 },
      L3: { metCount: 5, totalCount: 5 },
      L4: { metCount: 1, totalCount: 5 },
      L5: { metCount: 0, totalCount: 5 },
    },
    bullets: [
      {
        id: "sm_l3_alignment",
        text: "Keeps cross-functional peers aligned on operational cadence.",
        level: "L3",
        met: true,
      },
      {
        id: "sm_l4_reads_room",
        text: "Reads what is not being said in meetings.",
        level: "L4",
        met: false,
        gapRationale: "Sem registro de leitura de dinâmicas políticas sêniores.",
      },
    ],
  },
  {
    id: "business_acumen",
    name: "Business Acumen",
    required: "L3",
    assigned: "L3",
    levelMeta: DEMO_LEVEL_META.business_acumen,
    levelCoverage: {
      L1: { metCount: 0, totalCount: 0 },
      L2: { metCount: 0, totalCount: 0 },
      L3: { metCount: 5, totalCount: 5 },
      L4: { metCount: 2, totalCount: 5 },
      L5: { metCount: 0, totalCount: 5 },
    },
    bullets: [
      {
        id: "ba_l3_vocab",
        text: "Uses correct business vocabulary.",
        level: "L3",
        met: true,
      },
      {
        id: "ba_l4_challenge",
        text: "Challenges ideas that don't align with business reality.",
        level: "L4",
        met: false,
      },
    ],
  },
  {
    id: "hr_fluency",
    name: "HR Fluency",
    required: "L3",
    assigned: "L3",
    levelMeta: DEMO_LEVEL_META.hr_fluency,
    levelCoverage: {
      L1: { metCount: 0, totalCount: 0 },
      L2: { metCount: 0, totalCount: 0 },
      L3: { metCount: 5, totalCount: 5 },
      L4: { metCount: 0, totalCount: 5 },
      L5: { metCount: 0, totalCount: 5 },
    },
    bullets: [
      {
        id: "hr_l3_vocab",
        text: "Uses HR vocabulary correctly.",
        level: "L3",
        met: true,
      },
      {
        id: "hr_l4_philosophy",
        text: "Articulates comp philosophy vs structure.",
        level: "L4",
        met: false,
      },
    ],
  },
  {
    id: "product_sense",
    name: "Product Sense",
    required: "L3",
    assigned: "L4",
    levelMeta: DEMO_LEVEL_META.product_sense,
    levelCoverage: {
      L1: { metCount: 0, totalCount: 0 },
      L2: { metCount: 0, totalCount: 0 },
      L3: { metCount: 5, totalCount: 5 },
      L4: { metCount: 4, totalCount: 5 },
      L5: { metCount: 0, totalCount: 5 },
    },
    bullets: [
      {
        id: "ps_l4_simplest_tool",
        text: "Chooses the simplest tool that solves the problem at stake.",
        level: "L4",
        met: true,
      },
      {
        id: "ps_l4_payback",
        text: "Treats tools as investments, evaluates by payback period.",
        level: "L4",
        met: false,
      },
    ],
  },
];

const NO_HIRE_SENSITIVITY: SensitivityResult[] = [
  {
    axis: "compWay",
    perturbations: [
      {
        evidenceId: "tenure",
        delta: 3.0,
        resultingScore: 0.69,
        flipsVerdict: false,
      },
      {
        evidenceId: "founder",
        delta: 3.5,
        resultingScore: 0.55,
        flipsVerdict: false,
      },
      {
        evidenceId: "tenure+founder",
        delta: 3.0,
        resultingScore: 0.94,
        flipsVerdict: true,
      },
    ],
    fragilityRatio: "0 de 2 isolados",
    fragile: false,
  },
];

// Pre-computed axis values to keep this file free of server imports.
// Validated by tests in shared/labs/__tests__/three-axis-demo.test.ts.
function round2(n: number): number {
  return Number(n.toFixed(2));
}

function buildAxes(evidences: Evidence[], framework: FrameworkSkill[]): AxisScore[] {
  const scoreFor = (dim: string): number => {
    const ev = evidences.find((e) => e.id === dim);
    return ev?.score != null ? ev.score / 5 : 0;
  };
  // v2 weights: trajectory (0.70) replaces company_era (0.35) + growth_within (0.35) for GCA;
  // trajectory (0.30) replaces company_era (0.30) for CompWay. Mirrors AXIS_EVIDENCE_MAP in
  // shared/labs/axis-weights.ts — keep in sync if production aggregator changes.
  const gcaValue = scoreFor("academic") * 0.3 + scoreFor("trajectory") * 0.7;
  const cwValue =
    scoreFor("founder") * 0.4 + scoreFor("tenure") * 0.3 + scoreFor("trajectory") * 0.3;
  const parse = (l: string): number => Number(l.slice(1));
  const skillsValue = Math.min(...framework.map((s) => parse(s.assigned)));
  const skillsThreshold = Math.min(...framework.map((s) => parse(s.required)));

  const gca: AxisScore = {
    id: "gca",
    value: round2(gcaValue),
    threshold: 0.6,
    margin: round2(gcaValue - 0.6),
    pass: gcaValue >= 0.6,
    closeCall: Math.abs(gcaValue - 0.6) <= 0.05,
  };
  const compWay: AxisScore = {
    id: "compWay",
    value: round2(cwValue),
    threshold: 0.65,
    margin: round2(cwValue - 0.65),
    pass: cwValue >= 0.65,
    closeCall: Math.abs(cwValue - 0.65) <= 0.05,
  };
  const skills: AxisScore = {
    id: "skills",
    value: skillsValue,
    threshold: skillsThreshold,
    margin: skillsValue - skillsThreshold,
    pass: skillsValue >= skillsThreshold,
    closeCall: skillsValue - skillsThreshold === 0,
  };
  return [gca, compWay, skills];
}

function buildRecommendation(
  evidences: Evidence[],
  framework: FrameworkSkill[],
  sensitivity: SensitivityResult[],
  overrides: Pick<ThreeAxisRecommendation, "decisionReason" | "levelRationale">,
): ThreeAxisRecommendation {
  const axes = buildAxes(evidences, framework);
  const allPass = axes.every((a) => a.pass);
  const [gca, compWay, skills] = axes;
  // Demo path mirrors the production aggregator: same shared helpers
  // decide exception + borderline tiers so demo decisions never diverge
  // from prod for the same evidence shapes.
  const exceptional = !allPass ? findExceptionalAxis(gca, compWay, skills) : null;
  const borderline = !allPass && !exceptional ? findBorderlineAxis(gca, compWay) : null;
  const pass = allPass || exceptional !== null || borderline !== null;
  return {
    decision: pass ? "interview" : "no-interview",
    decisionReason: overrides.decisionReason,
    ...(exceptional ? { exceptionalAxis: { id: exceptional.id, value: exceptional.value } } : {}),
    ...(borderline ? { borderlineAxis: { id: borderline.id, value: borderline.value } } : {}),
    axes,
    evidences,
    sensitivity,
    frameworkSkills: framework,
    appliedLevel: `L${skills.value}` as "L1" | "L2" | "L3" | "L4" | "L5" | "L6",
    levelRationale: overrides.levelRationale,
  };
}

export const DEMO_INTERVIEW: ThreeAxisRecommendation = buildRecommendation(
  HIRE_EVIDENCES,
  HIRE_FRAMEWORK,
  HIRE_SENSITIVITY,
  {
    // v2 cutover: CW drops from 0.83 to 0.80 because trajectory=4.0 replaces
    // company_era=4.5 (old weight 0.30). GCA stays at 0.80 (unchanged).
    decisionReason:
      "GCA 0.80 (+0.20) e Comp Way 0.80 (+0.15). Skills L4 cobre o framework FDE. Encaminha para entrevista com folga nos três axes.",
    levelRationale:
      "5 skills no framework; mínimo atribuído L4; 4/5 bullets batidos em L4 para a maioria das skills; L5 exigiria profundidade ainda não surfaceada no perfil.",
  },
);

/**
 * Exception-lane demo: a candidate with elite academic + employer pedigree
 * (high GCA), but short tenures and no builder track-record (mediocre Comp
 * Way). Under the old AND-rule this person would be a "no-interview" — the
 * new exception lane catches them as a clear outlier on a single signal.
 *
 * Math (validated against `buildAxes`):
 *   GCA  = 0.30·(5/5) + 0.70·(4.5/5) = 0.30 + 0.63 = 0.93
 *   CW   = 0.40·(2/5) + 0.30·(2/5) + 0.30·(4.5/5) = 0.16 + 0.12 + 0.27 = 0.55
 *   Skills = L4 (HIRE_FRAMEWORK).
 * Result: GCA crosses the 0.85 cutoff, Comp Way fails its 0.65 threshold,
 * Skills passes — the exception lane fires with `exceptionalAxis = gca`.
 * (v2 cutover: company_era=5.0 + growth_within=4.0 merged into trajectory=4.5)
 */
const EXCEPTION_EVIDENCES: Evidence[] = HIRE_EVIDENCES.map((ev) => {
  switch (ev.id) {
    case "academic":
      return {
        ...ev,
        score: 5.0,
        selectedLevel: 5,
        sources: [
          {
            quote: "PhD em Computer Science — universidade Tier 1 internacional (top-5 global).",
            citation: "Universidade Tier 1 · doutorado",
          },
          {
            quote: "Bacharelado em Engenharia — universidade Tier 1 nacional, dean's list.",
            citation: "Universidade Tier 1 · graduação",
          },
        ],
      };
    case "trajectory":
      return {
        ...ev,
        // avg(company_era=5.0, growth_within=4.0) = 4.5 → GCA = 0.30+0.63 = 0.93 ≥ 0.85
        score: 4.5,
        selectedLevel: 5,
        sources: [
          {
            quote: "Senior Engineer @ FAANG durante a era de hipercrescimento de IA aplicada.",
            citation: "Empresa Tier 1 · fase de pico",
          },
          {
            quote: "Researcher @ Goldman Sachs em modelagem quantitativa.",
            citation: "Empresa Tier 1 · fase de pico",
          },
          {
            quote: "Promovido 1x em 18 meses no FAANG — IC4 → IC5 acima da curva.",
            citation: "Empresa Tier 1 · histórico de promoções",
          },
        ],
      };
    case "tenure":
      return {
        ...ev,
        score: 2.0,
        selectedLevel: 2,
        sources: [
          {
            quote:
              "5 empregadores em 7 anos — a permanência mais longa foi 18 meses, sem track de backbone.",
            citation: "histórico de empregadores",
          },
        ],
      };
    case "founder":
      return {
        ...ev,
        score: 2.0,
        selectedLevel: 2,
        sources: [
          {
            quote:
              "Sem experiência fundadora ou de early-stage; toda a carreira em ICs de empresas estabelecidas.",
            citation: "resumo do perfil",
          },
        ],
      };
    default:
      return ev;
  }
});

export const DEMO_INTERVIEW_EXCEPTION: ThreeAxisRecommendation = buildRecommendation(
  EXCEPTION_EVIDENCES,
  HIRE_FRAMEWORK,
  HIRE_SENSITIVITY,
  {
    // v2 cutover: CW = 0.40·(2/5) + 0.30·(2/5) + 0.30·(4.5/5) = 0.16+0.12+0.27 = 0.55
    decisionReason:
      "Entrevistar — excepcional em GCA (0.93), apesar de Comp Way abaixo do corte (0.55 vs 0.65). Pedigree acadêmico + empregadores tier-1 carregam o sinal cognitivo bem acima do bar; estabilidade e fit cultural ficam a cargo da entrevista validar.",
    levelRationale:
      "Skills passam o framework FDE em L4 com folga nos bullets de PM/Stakeholder. A exceção é movida pelo GCA, não pelo eixo de Skills.",
  },
);

/**
 * Borderline-lane demo: GCA lands in [0.80, 0.85) — strong but not
 * exceptional. Comp Way fails its threshold. Under the strict AND rule
 * this candidate would silently disappear; under the soft cutoff it
 * surfaces as "interview · borderline" so a recruiter triages instead
 * of auto-rejecting.
 *
 * Math (v2 weights):
 *   GCA  = 0.30·(4/5) + 0.70·(4/5) = 0.24 + 0.56 = 0.80
 *   CW   = 0.40·(2/5) + 0.30·(2/5) + 0.30·(4/5) = 0.16 + 0.12 + 0.24 = 0.52
 *   Skills = L4.
 * Result: GCA ∈ [0.80, 0.85), Comp Way fails 0.65, Skills passes target —
 * neither AND-pass nor exception fires; borderline lane catches it.
 * (v2 cutover: company_era=4.0 + growth_within=4.0 merged into trajectory=4.0)
 */
const BORDERLINE_EVIDENCES: Evidence[] = HIRE_EVIDENCES.map((ev) => {
  switch (ev.id) {
    case "academic":
      return {
        ...ev,
        score: 4.0,
        selectedLevel: 4,
        sources: [
          {
            quote: "Bacharelado em curso competitivo — Universidade Tier 1 nacional.",
            citation: "Universidade Tier 1 · graduação",
          },
        ],
      };
    case "trajectory":
      return {
        ...ev,
        // avg(company_era=4.0, growth_within=4.0) = 4.0 → GCA = 0.24+0.56 = 0.80 ∈ [0.80, 0.85)
        score: 4.0,
        selectedLevel: 4,
        sources: [
          {
            quote: "Senior IC @ Tier 1 ao longo de 4 anos durante crescimento do negócio.",
            citation: "Empresa Tier 1 · fase estável",
          },
          {
            quote: "Promovido 1x em 4 anos no Tier 1 — IC4 → IC5.",
            citation: "Empresa Tier 1 · histórico de promoções",
          },
        ],
      };
    case "tenure":
      return {
        ...ev,
        score: 2.0,
        selectedLevel: 2,
        sources: [
          {
            quote:
              "3 empregadores em 5 anos; permanência mais longa de 28 meses, abaixo do esperado para senioridade.",
            citation: "histórico de empregadores",
          },
        ],
      };
    case "founder":
      return {
        ...ev,
        score: 2.0,
        selectedLevel: 2,
        sources: [
          {
            quote:
              "Sem experiência fundadora; algumas passagens por times pequenos mas sempre como funcionário.",
            citation: "resumo do perfil",
          },
        ],
      };
    default:
      return ev;
  }
});

export const DEMO_BORDERLINE: ThreeAxisRecommendation = buildRecommendation(
  BORDERLINE_EVIDENCES,
  HIRE_FRAMEWORK,
  HIRE_SENSITIVITY,
  {
    decisionReason:
      "Entrevistar (borderline) — GCA (0.80) entre o corte regular (0.60) e a bar de excepcionalidade (0.85), apesar de Comp Way abaixo do corte (0.52 vs 0.65). Recomenda-se entrevista com confiança menor que um sinal claramente excepcional; revisor humano valida na conversa.",
    levelRationale:
      "Skills passam o framework FDE em L4. A entrada é via lane borderline no eixo cognitivo, não via excepcionalidade.",
  },
);

export const DEMO_NO_INTERVIEW: ThreeAxisRecommendation = buildRecommendation(
  NO_HIRE_EVIDENCES,
  NO_HIRE_FRAMEWORK,
  NO_HIRE_SENSITIVITY,
  {
    decisionReason:
      "Reprovado em Comp Way (0.45 vs corte 0.65). Estabilidade 2.0 e Fundador 1.5 são os maiores detratores. GCA passa com 0.67, mas não dispara a regra de excepcionalidade (cutoff 0.85), então não há lane de entrada nesta combinação.",
    levelRationale: "3 de 5 skills em L3; 2 em L4 (PM, Product). Mínimo atribuído L3.",
  },
);
